// src/services/signalrService.js
import * as signalR from '@microsoft/signalr';

/**
 * WeakMap to store wrapped callback references without mutating the original
 * callback functions. This avoids polluting externally-owned function objects
 * with `._wrapped` properties and allows proper GC when callbacks are removed.
 */
const wrappedCallbackMap = new WeakMap();

class SignalRService {
  connection = null;
  isConnecting = false;
  connectionPromise = null;

  /** Map<eventName, Set<originalCallback>> */
  eventHandlers = new Map();

  /** Rolling log of the last 100 received events (for debugging). */
  receivedEvents = [];

  // ── Public API ───────────────────────────────────────────────────────────

  /**
   * Connect to the SignalR hub.
   * Safe to call multiple times — returns the existing promise/connection if
   * already connecting or connected.
   */
  async connect(token) {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return this.connection;
    }

    if (this.isConnecting && this.connectionPromise) {
      return this.connectionPromise;
    }

    this.isConnecting = true;
    this.connectionPromise = this._createConnection(token);

    try {
      await this.connectionPromise;
      return this.connection;
    } finally {
      this.isConnecting = false;
      this.connectionPromise = null;
    }
  }

  isConnected() {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }

  async disconnect() {
    if (!this.connection) return;

    try {
      await this.connection.stop();
    } catch (err) {
      console.error('SignalR: error during disconnect:', err);
    }

    this.connection = null;
    this.eventHandlers.clear();
    this.receivedEvents = [];
  }

  /**
   * Register a callback for a hub event.
   * Duplicate registrations of the same callback are silently ignored.
   */
  on(event, callback) {
    if (!this.connection) {
      console.warn('SignalR: connection not initialised — cannot register event:', event);
      return;
    }

    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }

    const handlers = this.eventHandlers.get(event);

    // Prevent duplicate registration of the same callback
    if (handlers.has(callback)) return;

    handlers.add(callback);

    // Create a wrapped version that logs to the debug buffer
    const wrappedCallback = (...args) => {
      this._logEvent(event, args);
      callback(...args);
    };

    // Store the mapping so we can remove the correct wrapped fn later
    wrappedCallbackMap.set(callback, wrappedCallback);

    this.connection.on(event, wrappedCallback);
  }

  /**
   * Unregister a callback (or all callbacks for an event if no callback given).
   */
  off(event, callback) {
    if (!this.connection) return;

    if (callback) {
      const wrapped = wrappedCallbackMap.get(callback);
      this.connection.off(event, wrapped ?? callback);
      wrappedCallbackMap.delete(callback);

      const handlers = this.eventHandlers.get(event);
      if (handlers) {
        handlers.delete(callback);
        if (handlers.size === 0) this.eventHandlers.delete(event);
      }
    } else {
      // Remove ALL handlers for this event
      const handlers = this.eventHandlers.get(event);
      if (handlers) {
        handlers.forEach((cb) => {
          const wrapped = wrappedCallbackMap.get(cb);
          if (wrapped) {
            this.connection.off(event, wrapped);
            wrappedCallbackMap.delete(cb);
          }
        });
      }
      this.connection.off(event);
      this.eventHandlers.delete(event);
    }
  }

  async invoke(method, ...args) {
    if (!this.isConnected()) {
      throw new Error('SignalR: cannot invoke — connection not ready');
    }

    try {
      return await this.connection.invoke(method, ...args);
    } catch (err) {
      console.error(`SignalR: failed to invoke "${method}":`, err);
      throw err;
    }
  }

  // ── Debug helpers ────────────────────────────────────────────────────────

  getReceivedEvents() {
    return this.receivedEvents;
  }

  clearReceivedEvents() {
    this.receivedEvents = [];
  }

  getConnectionDetails() {
    return {
      isConnected: this.isConnected(),
      connectionId: this.connection?.connectionId ?? null,
      connectionState: this.connection?.state ?? null,
      registeredEvents: Array.from(this.eventHandlers.keys()),
      eventCounts: Object.fromEntries(
        Array.from(this.eventHandlers.entries()).map(([event, handlers]) => [
          event,
          handlers.size,
        ])
      ),
      recentReceivedEvents: this.receivedEvents.slice(-10),
    };
  }

  // ── Private helpers ──────────────────────────────────────────────────────

  async _createConnection(token) {
    // Tear down any stale connection first
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (e) {
        console.warn('SignalR: failed to stop existing connection:', e);
      }
      // Clean up all wrapped callback mappings from the old connection
      this.eventHandlers.forEach((handlers) => {
        handlers.forEach((cb) => wrappedCallbackMap.delete(cb));
      });
      this.eventHandlers.clear();
    }

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl('https://backend.nessefy.com/chatHub', {
        // accessTokenFactory supplies the JWT for both the negotiate HTTP
        // request and the WebSocket upgrade — this is how the hub resolves
        // Context.UserIdentifier via CustomUserIdProvider.
        accessTokenFactory: () => token,
        // skipNegotiation MUST be true when forcing WebSockets-only.
        // With skipNegotiation:false the library does an HTTP negotiate first.
        // The negotiate request doesn't always carry the token correctly,
        // leaving the connection unauthenticated and Context.UserIdentifier null.
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect({
        // Exponential back-off: 0 s, 2 s, 10 s, 30 s, 60 s …
        nextRetryDelayInMilliseconds: (ctx) =>
          Math.min(1000 * Math.pow(2, ctx.previousRetryCount), 60_000),
      })
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    this._setupConnectionHandlers();

    try {
      await this.connection.start();
      // Re-register all previously stored handlers on the new connection
      this._reregisterHandlers();
      return this.connection;
    } catch (err) {
      console.error('SignalR: connection failed:', err);
      throw err;
    }
  }

  _setupConnectionHandlers() {
    this.connection.onreconnected(() => {
      this._reregisterHandlers();
    });

    this.connection.onreconnecting((error) => {
      if (error) console.warn('SignalR: reconnecting...', error);
    });

    this.connection.onclose(() => {
      this.isConnecting = false;
    });
  }

  /**
   * Re-attach all stored wrapped callbacks after a reconnect.
   * Removes stale SignalR-level handlers first to prevent duplicates.
   */
  _reregisterHandlers() {
    if (!this.connection) return;

    for (const [event, handlers] of this.eventHandlers) {
      // Remove all existing SignalR-level handlers for this event
      this.connection.off(event);

      // Re-register each stored wrapped callback
      handlers.forEach((cb) => {
        const wrapped = wrappedCallbackMap.get(cb);
        if (wrapped) this.connection.on(event, wrapped);
      });
    }
  }

  _logEvent(event, data) {
    this.receivedEvents.push({ event, timestamp: new Date(), data });
    // Cap the buffer at 100 entries
    if (this.receivedEvents.length > 100) {
      this.receivedEvents = this.receivedEvents.slice(-100);
    }
  }
}

const signalRService = new SignalRService();
export default signalRService;